import type { NavItem, SubNavItem } from '../types';
import { isTabShown } from '../navResolution';

export interface ShortcutOption {
  label: string;
  href: string;
}

export interface ShortcutGroup {
  label: string;
  options: ShortcutOption[];
}

type IsAllowed = (access: NavItem['onlyShownTo']) => boolean;

const isLinkable = (href?: string) => Boolean(href) && href.startsWith('/') && !href.includes('[');

const isVisibleRow = (row: SubNavItem, isAllowed: IsAllowed) =>
  row.isShownInBar !== false && row.isDisabled !== true && !row.isCustomElement && isAllowed(row.onlyShownTo);

const getRowOptions = (row: SubNavItem, isAllowed: IsAllowed): ShortcutOption[] => {
  const tabs = (row.subNavs ?? []).filter(
    (tab) => isTabShown(tab) && isAllowed(tab.onlyShownTo) && isLinkable(tab.href),
  );
  if (row.category) {
    return tabs.map((tab) => ({ label: tab.name, href: tab.href }));
  }
  if (tabs.length > 0) {
    return tabs.map((tab) => ({
      label: tab.name === 'Overview' ? row.name : `${row.name} › ${tab.name}`,
      href: tab.href,
    }));
  }
  return isLinkable(row.href) ? [{ label: row.name, href: row.href }] : [];
};

export const getShortcutGroups = (navigation: NavItem[], isAllowed: IsAllowed): ShortcutGroup[] => {
  const seen = new Set<string>();
  return navigation
    .filter((item) => isAllowed(item.onlyShownTo))
    .map((item) => {
      const rows = (item.subNavs ?? []).filter((row) => isVisibleRow(row, isAllowed));
      const candidates =
        rows.length > 0
          ? rows.flatMap((row) => getRowOptions(row, isAllowed))
          : isLinkable(item.href)
            ? [{ label: item.name, href: item.href }]
            : [];
      const options = candidates.filter((option) => {
        if (seen.has(option.href)) return false;
        seen.add(option.href);
        return true;
      });
      return { label: item.name || 'Settings', options };
    })
    .filter((group) => group.options.length > 0);
};
